/**
 * Transforms CustomWidgetPanel draft data into a widget creation payload
 */

import type { WidgetTypeOption, DatePreset } from "@/components/molecules/ManualWidgetForm";
import type { Level, Segment, Metric, Service } from "@/modules/product/types";
import { type DateRangeValue, toStartOfDay, toEndOfDay, formatDate, getDateRangeForPreset } from "./dateUtils";
import { type ServiceParameters, type ProductInfo, buildServiceParameters, buildServiceUrl, resolvePrimaryService, buildDataSources } from "./serviceUtils";
import { PageTab } from "@/modules/pages";
import { Widget } from "../types";
import { DataFilter, DraftDataFilter, isDraftFilterComplete } from "../types/dataFilters";

const AUXILIARY_METRIC_NAMES = new Set(["productName", "campaignName", "clientName", "adName", "accountName"]);
const DEFAULT_WIDGET_COLORS = ["#228995", "#74c4c6", "#f1883c", "#dc5c1f", "#eab234", "#71c7a7", "#9f4462", "#e15e64"];

interface CustomWidgetDraft {
    title: string;
    description: string;
    widgetType: string | null;
    productIds: number[];
    levelId: number;
    segmentId: number | null;
    metricIds: number[];
    dynamicMetricIds?: number[];
    dynamicMetricNames?: string[];
    groupBySegment: boolean;
    drilldownBySegment: boolean;
    publishToAll: boolean;
    widgetColors: string[];
    levelName: string;
    segmentName?: string;
    metricNames?: string[];
    filterByProduct: boolean;
    filterByDimension: boolean;
    tableSummaryRow?: "none" | "sum" | "average";
    usePageDate: boolean;
    datePreset?: DatePreset;
    customDateRange?: { from?: Date | null; to?: Date | null };
    productLevel?: boolean;
    showMetricSelector?: boolean;
    enableAIInsights?: boolean;
    enableSuperUserInsights?: boolean;
    publishInsights?: boolean;
    realWidgetType?: string;
    dataFilters?: DraftDataFilter[];
}

interface TransformOptions {
    pageDateRange?: DateRangeValue | null;
}

export interface WidgetCreationPayload {
    title: string;
    description: string;
    type: "chart" | "table" | "ticker";
    pageId: number;
    clientId: number;
    createdBy?: string;
    publishedToAll?: boolean;
    metadata?: Record<string, unknown>;
    service: {
        url: string;
        parameters: ServiceParameters;
        method: "GET" | "POST" | "PUT" | "DELETE";
        headers: Record<string, string>;
        body: Record<string, unknown>;
    };
    chartType?: "line" | "bar" | "funnel" | "pie" | "doughnut" | "bubble" | "stacked-bar-line";
    metrics?: string[];
    breakdownBy?: "level" | "segment";
    widgetInfo?: string;
    expandable?: boolean;
    colors?: string[];
    datePeriod?: string;
    filterByProduct?: boolean;
    filterByDimension?: boolean;
    tag?: string | null;
    filters?: {
        dateRange: {
            from: string;
            to: string;
        };
    };
}

type DateConfig = {
    startDate: string;
    endDate: string;
    widgetDatePeriod: string;
};

const buildDateConfig = (draft: CustomWidgetDraft, options?: TransformOptions): DateConfig => {
    if (draft.usePageDate) {
        const from = options?.pageDateRange?.from;
        const to = options?.pageDateRange?.to;
        const range = from && to ? { from: toStartOfDay(new Date(from)), to: toEndOfDay(new Date(to)) } : getDateRangeForPreset("last_30_days");

        return {
            startDate: formatDate(range.from),
            endDate: formatDate(range.to),
            widgetDatePeriod: "dashboard_date",
        };
    }

    if (draft.datePreset === "custom" && draft.customDateRange?.from && draft.customDateRange?.to) {
        return {
            startDate: formatDate(toStartOfDay(new Date(draft.customDateRange.from))),
            endDate: formatDate(toEndOfDay(new Date(draft.customDateRange.to))),
            widgetDatePeriod: "custom",
        };
    }

    const presetRange = getDateRangeForPreset(draft.datePreset);
    return {
        startDate: formatDate(presetRange.from),
        endDate: formatDate(presetRange.to),
        widgetDatePeriod: draft.datePreset ?? "last_30_days",
    };
};

/**
 * Maps frontend widget types to backend widget types
 */
function mapWidgetType(frontendType: string | null): "chart" | "table" | "ticker" {
    switch (frontendType) {
        case "ticker":
            return "ticker";
        case "table":
            return "table";
        case "stacked-bar-line":
        case "bar":
        case "line":
        case "pie":
        case "funnel":
        case "bubble":
            return "chart";
        default:
            return "chart";
    }
}

/**
 * Maps frontend chart types to backend chart types
 */
function mapChartType(frontendType: string | null): "line" | "bar" | "funnel" | "pie" | "doughnut" | "bubble" | "stacked-bar-line" | undefined {
    switch (frontendType) {
        case "bar":
            return "bar";
        case "line":
            return "line";
        case "pie":
            return "doughnut";
        case "funnel":
            return "funnel";
        case "bubble":
            return "bubble";
        case "stacked-bar-line":
            return "stacked-bar-line";
        default:
            return undefined;
    }
}

/**
 * Transforms CustomWidgetPanel draft to widget creation payload
 */
export function transformDraftToWidgetPayload(
    draft: CustomWidgetDraft,
    pageId: number,
    currentTab: PageTab | null,
    levels: Level[],
    segments: Segment[],
    metrics: Metric[],
    products: ProductInfo[] = [],
    clientId: number,
    options?: TransformOptions & { userId?: string }
): WidgetCreationPayload {
    const levelName = levels.find((l) => l.id === draft.levelId)?.name || "";
    const segmentName = draft.segmentId ? segments.find((s) => s.id === draft.segmentId)?.name : undefined;

    // Get metric names
    const metricNames = draft.metricIds
        .map((id) => {
            const metric = metrics.find((m) => m.id === id);
            return metric?.name || metric?.id?.toString();
        })
        .filter(Boolean) as string[];

    // Metric names marked as hidden-by-default ("dynamic"). Stored as names so they
    // can be matched against table column.metric at render time.
    const dynamicMetricNames = (draft.dynamicMetricIds ?? [])
        .map((id) => metrics.find((m) => m.id === id)?.name)
        .filter(Boolean) as string[];

    const levelDimensionMap: Record<string, string> = {
        campaign: "campaignName",
        client: "clientName",
        ad: "adName",
        account: "accountName",
    };
    const levelDimension = levelDimensionMap[levelName.toLowerCase()];

    const allMetrics = levelDimension ? [levelDimension, ...metricNames] : metricNames;

    const EXPANDABLE_LEVELS = ["campaign", "client"];

    let breakdownBy: "level" | "segment" | undefined;
    const lowerLevel = levelName.toLowerCase();
    const hasSegment = !!draft.segmentName;

    if (draft.groupBySegment && draft.segmentId) {
        breakdownBy = "segment";
    } else if (EXPANDABLE_LEVELS.includes(lowerLevel)) {
        breakdownBy = "level";
    } else if (lowerLevel === "keyword" && !hasSegment) {
        breakdownBy = "level";
    } else {
        breakdownBy = undefined;
    }

    const { startDate, endDate, widgetDatePeriod } = buildDateConfig(draft, options);

    const productById = products.reduce<Record<number, ProductInfo>>((acc, product) => {
        acc[product.id] = product;
        return acc;
    }, {});

    const levelsByServiceId = levels.reduce<Record<number, Level[]>>((acc, level) => {
        if (level.serviceId === undefined) {
            return acc;
        }
        if (!acc[level.serviceId]) {
            acc[level.serviceId] = [];
        }
        acc[level.serviceId].push(level);
        return acc;
    }, {});

    const serviceEntries = products.flatMap((product) =>
        (product.services || []).map((service) => ({
            productId: product.id,
            productName: product.name,
            serviceId: service.id,
            serviceSlug: service.url,
            serviceDisplayName: service.name,
        }))
    );

    const levelNameLower = levelName.toLowerCase();

    const primaryServiceEntry = resolvePrimaryService({
        serviceEntries,
        levelsByServiceId,
        levelNameLower,
        selectedLevelServiceId: levels.find((level) => level.id === draft.levelId)?.serviceId,
        firstMetricServiceId: draft.metricIds.length ? metrics.find((m) => m.id === draft.metricIds[0])?.serviceId : undefined,
        firstProductId: draft.productIds[0],
    });

    const primaryProductId = primaryServiceEntry?.productId ?? draft.productIds[0];
    const primaryProduct = primaryProductId !== undefined ? productById[primaryProductId] : undefined;
    const primaryServiceUrl = buildServiceUrl(primaryServiceEntry?.serviceSlug);

    const serviceParams = buildServiceParameters({
        levelName,
        levelId: draft.levelId,
        segmentName,
        segmentId: draft.segmentId,
        metricIds: draft.metricIds,
        startDate,
        endDate,
    });

    const dataSources = buildDataSources({
        productIds: draft.productIds,
        productById,
        serviceEntries,
        levelsByServiceId,
        levelNameLower,
        primaryServiceSlug: primaryServiceEntry?.serviceSlug,
        serviceParams,
    });

    const service = {
        url: primaryServiceUrl,
        productId: primaryProduct?.id,
        productName: primaryProduct?.name || "",
        parameters: serviceParams,
        method: "GET" as const,
        headers: {},
        body: {},
        dataSources: dataSources.length > 0 ? dataSources : undefined,
    };

    const activeDataFilters: DataFilter[] = (draft.dataFilters ?? []).filter(isDraftFilterComplete);
    const dataFiltersMeta = activeDataFilters.length > 0 ? { "data-filters": activeDataFilters } : {};

    const baseMetadata = {
        "enableAIInsights": draft.enableAIInsights ?? false,
        "publishInsights": draft.enableAIInsights ? (draft.publishInsights ?? false) : false,
        ...dataFiltersMeta,
    };

    const metadata: Record<string, unknown> | undefined =
        mapWidgetType(draft.widgetType) === "table"
            ? {
                  ...baseMetadata,
                  "table-summary-row": draft.tableSummaryRow ?? "none",
                  "product-level": draft.productLevel ?? undefined,
                  dynamicMetrics: dynamicMetricNames.length > 0 ? dynamicMetricNames : undefined,
              }
            : draft.widgetType === "stacked-bar-line"
              ? {
                    ...baseMetadata,
                    "simple-bar-line": true,
                }
              : {
                    ...baseMetadata,
                    "showMetricSelector": draft.showMetricSelector ?? false,
                };

    // For custom widget types, use the real backend type directly
    const isCustom = draft.widgetType === "custom" && draft.realWidgetType;
    const resolvedType = isCustom ? draft.realWidgetType! : mapWidgetType(draft.widgetType);
    const resolvedChartType = isCustom ? undefined : mapChartType(draft.widgetType);

    return {
        title: draft.title,
        description: draft.description,
        type: resolvedType as any,
        pageId,
        clientId,
        ...(options?.userId ? { createdBy: options.userId } : {}),
        publishedToAll: draft.publishToAll,
        tag: currentTab?.tags?.[0] || undefined,
        metadata,
        service,
        chartType: resolvedChartType,
        metrics: allMetrics.length > 0 ? allMetrics : undefined,
        breakdownBy,
        widgetInfo: draft.description,
        expandable: true,
        colors: draft.widgetColors,
        datePeriod: widgetDatePeriod,
        filterByProduct: Boolean(draft?.filterByProduct),
        filterByDimension: Boolean(draft?.filterByDimension),
    };
}

/**
 * Extracts draft data from a widget for editing
 */
export function extractWidgetToDraft(widget: any, metrics: Metric[], services: Service[]): Partial<CustomWidgetDraft> {
    const widgetType = resolveWidgetType(widget);
    const productIds = extractProductIds(widget);
    const levelName = widget.service?.parameters?.level;
    const segmentName = widget.service?.parameters?.segment;
    const levelId = typeof widget.service?.parameters?.levelId === "number" ? widget.service.parameters.levelId : undefined;
    const segmentId = typeof widget.service?.parameters?.segmentId === "number" ? widget.service.parameters.segmentId : undefined;
    const metricNames = extractMetricNames(widget);
    const groupBySegment = widget.breakdownBy === "segment";
    const widgetColors = Array.isArray(widget.colors) && widget.colors.length > 0 ? widget.colors : DEFAULT_WIDGET_COLORS;

    const datePeriod = widget.datePeriod as string | undefined;
    const startDate = widget.service?.parameters?.startDate;
    const endDate = widget.service?.parameters?.endDate;

    const servicesByProductId = services.reduce<Record<number, number[]>>((acc, service) => {
        (acc[service.productId] ??= []).push(service.id);
        return acc;
    }, {});

    const getAvailableMetrics = (): Metric[] => {
        if (!productIds.length || !levelId) return [];

        // Get metrics for each selected product
        const metricsByProduct: Metric[][] = productIds.map((productId) => {
            const serviceIdsForProduct = new Set(servicesByProductId[productId] ?? []);
            return metrics.filter((metric) => serviceIdsForProduct.has(metric.serviceId));
        });

        // Find intersection: metrics with matching names that exist in ALL selected products
        const [firstProductMetrics, ...restProductMetrics] = metricsByProduct;
        if (!firstProductMetrics) return [];

        const metricNamesInAllProducts = new Set(
            firstProductMetrics
                .map((m) => m.name)
                .filter((name) => restProductMetrics.every((productMetrics) => productMetrics.some((m) => m.name === name)))
        );

        return firstProductMetrics.filter((m) => metricNamesInAllProducts.has(m.name));
    };

    const availableMetricsForEdit = getAvailableMetrics();
    const metricIdsFromNames = metricNames
        .map((name) => availableMetricsForEdit.find((m) => m.name === name)?.id)
        .filter((id): id is number => typeof id === "number");

    const dynamicMetricNamesFromMeta: string[] = Array.isArray((widget?.metadata as Record<string, unknown>)?.["dynamicMetrics"])
        ? ((widget.metadata as Record<string, unknown>)["dynamicMetrics"] as string[])
        : [];
    const dynamicMetricIdsFromNames = dynamicMetricNamesFromMeta
        .map((name) => availableMetricsForEdit.find((m) => m.name === name)?.id)
        .filter((id): id is number => typeof id === "number");

    let usePageDate = false;
    let datePreset: DatePreset | undefined;
    let customDateRange: { from?: Date | null; to?: Date | null } | undefined;

    if (datePeriod === "dashboard_date") {
        usePageDate = true;
    } else if (datePeriod === "custom" && startDate && endDate) {
        datePreset = "custom";
        customDateRange = {
            from: new Date(startDate + "T00:00:00"),
            to: new Date(endDate + "T00:00:00"),
        };
    } else if (datePeriod && datePeriod !== "custom") {
        datePreset = datePeriod as DatePreset;
    }

    const metadata: Widget["metadata"] = widget?.metadata ?? {};
    const dataFiltersFromMeta = (metadata as Record<string, unknown>)?.["data-filters"];
    return {
        title: widget.title ?? "",
        description: widget.description ?? "",
        widgetType,
        productIds,
        levelId,
        levelName,
        segmentId: segmentId ?? null,
        segmentName,
        metricNames,
        metricIds: metricIdsFromNames,
        dynamicMetricIds: dynamicMetricIdsFromNames,
        dynamicMetricNames: dynamicMetricNamesFromMeta,
        groupBySegment,
        drilldownBySegment: false,
        publishToAll: widget.publishedToAll ?? false,
        widgetColors,
        usePageDate,
        datePreset,
        customDateRange,
        filterByProduct: Boolean(widget.filterByProduct),
        filterByDimension: Boolean(widget.filterByDimension),
        tableSummaryRow: metadata?.["table-summary-row"] ?? "none",
        productLevel: Boolean(metadata?.["product-level"]),
        showMetricSelector: metadata?.["showMetricSelector"] ?? false,
        enableAIInsights: metadata?.["enableAIInsights"] ?? false,
        enableSuperUserInsights: metadata?.["enableSuperUserInsights"] ?? false,
        publishInsights: metadata?.["publishInsights"] ?? false,
        dataFilters: Array.isArray(dataFiltersFromMeta) ? (dataFiltersFromMeta as DraftDataFilter[]) : [],
    };
}

const resolveWidgetType = (widget: any): WidgetTypeOption | null => {
    if (!widget) return null;

    if (widget.type === "ticker") return "ticker";
    if (widget.type === "table") return "table";
    if (widget.type === "chart") {
        const chartType = (widget.chartType || "").toLowerCase();
        switch (chartType) {
            case "line":
                return "line";
            case "funnel":
                return "funnel";
            case "doughnut":
            case "pie":
                return "pie";
            case "bubble":
                return "bubble";
            case "stacked-bar-line":
                return "stacked-bar-line";
            case "bar":
            default:
                return "bar";
        }
    }

    return null;
};

const extractProductIds = (widget: any): number[] => {
    const productIds: number[] = [];

    const primaryProductId = widget?.service?.productId;
    if (primaryProductId) {
        productIds.push(primaryProductId);
    }

    if (widget?.service?.dataSources && Array.isArray(widget.service.dataSources)) {
        widget.service.dataSources.forEach((ds: any) => {
            if (ds?.productId && !productIds.includes(ds.productId)) {
                productIds.push(ds.productId);
            }
        });
    }

    return productIds;
};

const extractMetricNames = (widget: any): string[] => {
    if (!Array.isArray(widget?.metrics)) return [];

    return widget.metrics.filter((metricName: string) => !AUXILIARY_METRIC_NAMES.has(metricName));
};
